import { Injectable, Injector } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, BehaviorSubject, from, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { ApiV2Service } from './api-v2.service';
import { ActivityTrackingService } from './activity-tracking.service';

@Injectable()
export class AuthRefreshInterceptor implements HttpInterceptor {
  private refreshInFlight = false;
  private refreshed$ = new BehaviorSubject<boolean | null>(null);

  constructor(
    private apiV2Service: ApiV2Service,
    private injector: Injector
  ) {}

  /** Lazy — avoid circular DI with ActivityTrackingService / HttpClient. */
  private trackLogoutSafe(logoutType: string): Promise<void> {
    try {
      const activity = this.injector.get(ActivityTrackingService, null);
      return activity?.trackLogout(logoutType) || Promise.resolve();
    } catch {
      return Promise.resolve();
    }
  }

  private async logoutAfterActivity(
    logoutType: string,
    message: string
  ): Promise<void> {
    await this.trackLogoutSafe(logoutType);
    await this.apiV2Service.autologout('/auth', message, logoutType);
  }

  private interceptLogoutError(logoutType: string, message: string): void {
    void this.logoutAfterActivity(logoutType, message).catch(() => {
      /* ignore */
    });
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((err: unknown) => {
        if (!(err instanceof HttpErrorResponse)) {
          return throwError(() => err);
        }

        if (!this.isRefreshableAuthError(err)) {
          return throwError(() => err);
        }

        // Never try to refresh for auth / login-session endpoints (prevents loops
        // and false "session expired" during credential login when V1 password differs).
        const url = request.url ?? '';
        const isAuthEndpoint =
          url.includes('/auth/login') ||
          url.includes('/auth/refresh') ||
          url.includes('/custom-login') ||
          url.includes('/auth1/login') ||
          url.includes('/auth1/logout') ||
          url.includes('/auth1/startsession') ||
          url.includes('/other/logactivity') ||
          url.includes('/other/logactivitybatch') ||
          url.includes('/other/logevent');
        if (isAuthEndpoint) {
          return throwError(() => err);
        }

        // On login screen, never force logout — stale/background 401s are expected
        // for some users (expired local tokens) and must not block a new login.
        if (this.isOnAuthPage()) {
          return throwError(() => err);
        }

        // Backend idle guard (FE timers failed / sleep): session already closed server-side
        // Skip during account switch — outgoing V1 session is closed before the new one opens.
        if (this.isIdleSessionExpiredError(err)) {
          const switching = !!sessionStorage.getItem('account_switch_in_progress');
          const graceUntil = Number(sessionStorage.getItem('account_switch_grace') || 0);
          const inGrace = Date.now() < graceUntil;
          if (!switching && !inGrace && !this.isOnAuthPage()) {
            this.interceptLogoutError(
              'IDLE_3HR',
              'Your session has expired due to inactivity. Please login again.',
            );
          }
          return throwError(() => err);
        }

        // Avoid infinite retry loops
        if (request.headers.has('x-refresh-retried')) {
          return throwError(() => err);
        }

        // If refresh is already in progress, wait for it then retry once.
        if (this.refreshInFlight) {
          return this.refreshed$.pipe(
            filter((done): done is boolean => done !== null),
            take(1),
            switchMap((done) => {
              if (!done) {
                return throwError(() => err);
              }
              return next.handle(this.markRetried(request));
            })
          );
        }

        this.refreshInFlight = true;
        this.refreshed$.next(null);

        return from(this.apiV2Service.getSetCurrentUser()).pipe(
          switchMap((restored) => {
            if (!restored) {
              this.refreshInFlight = false;
              this.refreshed$.next(false);
              // Still on auth after refresh fail → do not wipe / show expired
              if (!this.isOnAuthPage()) {
                this.interceptLogoutError(
                  'TOKEN_FAIL',
                  'Your session has expired. Please login again.',
                );
              }
              return throwError(() => err);
            }
            this.refreshInFlight = false;
            this.refreshed$.next(true);
            return next.handle(this.markRetried(request));
          }),
          catchError((refreshErr) => {
            this.refreshInFlight = false;
            this.refreshed$.next(false);
            // Do not wipe session while alias switch is in progress
            const graceUntil = Number(sessionStorage.getItem('account_switch_grace') || 0);
            const inGrace = Date.now() < graceUntil;
            if (
              !sessionStorage.getItem('account_switch_in_progress') &&
              !inGrace &&
              !this.isOnAuthPage()
            ) {
              this.interceptLogoutError(
                'TOKEN_FAIL',
                'Your session has expired. Please login again.',
              );
            }
            return throwError(() => refreshErr);
          })
        );
      })
    );
  }

  private isOnAuthPage(): boolean {
    try {
      const href = String(location?.href || '');
      const path = String(location?.pathname || '');
      return (
        href.includes('/auth') ||
        path.startsWith('/auth') ||
        path.startsWith('/login')
      );
    } catch {
      return false;
    }
  }

  /** V1 ApiAuth idle guard — do not treat as Directus token refresh. */
  private isIdleSessionExpiredError(err: HttpErrorResponse): boolean {
    if (err.status !== 401 && err.status !== 419) {
      return false;
    }
    const message = this.extractErrorMessage(err).toLowerCase();
    // Only the inactivity message — not generic "session expired" (used when session already closed)
    return message.includes('inactivity');
  }

  private markRetried(req: HttpRequest<any>) {
    return req.clone({
      setHeaders: {
        'x-refresh-retried': '1',
      },
    });
  }

  private isRefreshableAuthError(err: HttpErrorResponse): boolean {
    if (err.status === 401 || err.status === 419) {
      return true;
    }

    if (err.status !== 403) {
      return false;
    }

    const message = this.extractErrorMessage(err).toLowerCase();
    return (
      message.includes('unauthorized') ||
      message.includes('invalid user') ||
      message.includes('invalid token') ||
      message.includes('token') ||
      message.includes('session')
    );
  }

  private extractErrorMessage(err: HttpErrorResponse): string {
    const body = err.error;
    if (typeof body === 'string') {
      return body;
    }
    return (
      body?.error?.message ||
      body?.error ||
      body?.message ||
      body?.errors?.[0]?.message ||
      err.message ||
      ''
    );
  }
}

